import { useEffect } from 'react';

interface HeadManagerProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogUrl?: string;
  ogImage?: string;
  scripts?: Array<{ type: string; content: string }>;
}

export default function HeadManager({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogType,
  ogUrl,
  ogImage,
  scripts,
}: HeadManagerProps) {
  useEffect(() => {
    if (title) {
      document.title = title;
    }

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    if (description) setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);

    if (ogTitle) setMeta('og:title', ogTitle, true);
    if (ogDescription) setMeta('og:description', ogDescription, true);
    if (ogType) setMeta('og:type', ogType, true);
    if (ogUrl) setMeta('og:url', ogUrl, true);
    if (ogImage) setMeta('og:image', ogImage, true);

    const linkSelectors: HTMLElement[] = [];
    if (canonical) {
      let linkEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!linkEl) {
        linkEl = document.createElement('link');
        linkEl.setAttribute('rel', 'canonical');
        document.head.appendChild(linkEl);
      }
      linkEl.setAttribute('href', canonical);
      linkSelectors.push(linkEl);
    }

    const scriptElements: HTMLScriptElement[] = [];
    if (scripts) {
      scripts.forEach((s) => {
        const script = document.createElement('script');
        script.type = s.type;
        script.textContent = s.content;
        document.head.appendChild(script);
        scriptElements.push(script);
      });
    }

    return () => {
      linkSelectors.forEach((el) => el.remove());
      scriptElements.forEach((el) => el.remove());
    };
  }, [title, description, keywords, canonical, ogTitle, ogDescription, ogType, ogUrl, ogImage, scripts]);

  return null;
}